// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getFilters, selectFiltersQuery } from '../../feature';

// Components
import { Select } from '@/ui';
import { StyledFilterRow, StyleInput } from './FiltersTableFiltersContainerStyles';
import { StyledSelect } from '../../components';

// Helpers
import { GetFiltersDTO } from '../../dtos';
import { getCategoriesOptions, getVisibilityOptions, getTypeOptions } from '../../helpers/getOptions';
import { FiltersTableFiltersFieldNames } from '../../helpers/types';
import { useCategoriesAutocomplete } from '../../hooks';

type IPartnersFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const PartnersFiltersContainer = ({ children, className }: IPartnersFiltersContainerProps) => {
  const { t } = useTranslation(['categories']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectFiltersQuery);
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({});

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetFiltersDTO(searchParams);
        dispatch(getFilters(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined, page: 1 };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelectFilter = (name: string) => (value: string | boolean) => {
    const newQuery = {
      ...query,
      [name]: value,
      page: 1,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const onSelectChange = (value: unknown, key: FiltersTableFiltersFieldNames) => {
    const newQuery = { ...query, [key]: value || undefined, page: 1 };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th />
        <th>
          <StyleInput
            placeholder={t('filters_table_name_filter_placeholder')}
            name={FiltersTableFiltersFieldNames.TITLE}
            onChange={handleSearch}
            value={query?.title || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getTypeOptions(t)}
            placeholder={t('filters_table_type_filter_placeholder')}
            onChange={handleSelectFilter(FiltersTableFiltersFieldNames.TYPE)}
            size="large"
            value={query?.type}
            allowClear
          />
        </th>
        <th />
        <th>
          <StyledSelect
            allowClear
            showSearch
            options={getCategoriesOptions(categoriesList ?? [])}
            onSearch={onCategoriesSearch}
            onChange={(v) => onSelectChange(v, FiltersTableFiltersFieldNames.CATEGORIES)}
            value={query?.categories}
            isMultiselect
            maxTagCount={1}
          />
        </th>
        <th>
          <Select
            options={getVisibilityOptions(t)}
            placeholder={t('filters_table_visibility_filter_placeholder')}
            onChange={handleSelectFilter(FiltersTableFiltersFieldNames.IS_ACTIVE)}
            size="large"
            value={query?.is_active}
            allowClear
          />
        </th>
        <th />
      </StyledFilterRow>
    </thead>
  );
};

export default memo(PartnersFiltersContainer);
